import { Select, Tooltip, makeStyles } from '@material-ui/core';
import { Fragment, useEffect, memo, useCallback, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import wordsToNumbers from 'words-to-numbers';
import { useAnalytics } from 'use-analytics';
import { CgArrowsVAlt } from 'react-icons/cg';
import { MdAccessible } from 'react-icons/md';
import { FaCheck } from 'react-icons/fa';
import _ from 'lodash';
import { availableCabinsRequest } from '../../../../../store/slices/cruise';
import { removeFromTrip, addToTrip } from '../../../../../store/slices/trip';
import useWindowSize from '../../../../../hooks/useWindowSize';
import AddButton from '../../../../AddButton';
import CruiseLineAdvisories from '../../../../CruiseLineAdvisories';
import DeckPlan from '../../../DeckPlans/DeckPlan';
import SpecificCabinSelect from './SpecificCabinSelect';
import Label from '../../../../Label';
import withSpinner from '../../../../../HOCs/withSpinner';
import useScrollToElement from '../../../../../hooks/useScrollToElement';
import Checkbox from '../../../../Checkbox';
import formatStateroomsForRequest from '../../../../../helpers/formatters/formatStateroomsForRequest';
import TripBookButton from '../../../../TripConfiguration/ViewTripConfigurationItems/TripBookButton';

const useStyles = makeStyles(() => ({
  tooltip: {
    fontFamily: 'var(--fontFamilyBody)',
  },
}));

const areDecksEqual = (deck, name2) => {
  const n1 = (deck.shipDeckName || '').toLowerCase();
  const n2 = (name2 || '').toLowerCase();
  if (n1 === n2) return true;
  // some ships (e.g. Navigator of the Seas) have number based deck names,
  // but it's called different in cabin numbers and in returned deck plans
  if (wordsToNumbers(n1) === wordsToNumbers(n2)) return true;
  if (
    wordsToNumbers(n1.replace('deck', '').trim()) === wordsToNumbers(n2.replace('deck', '').trim())
  )
    return true;

  return false;
};

const CabinSelect = ({
  itineraryId,
  cabinCategoryCode,
  cabinCategoryRateOptionCode,
  gradeno,
  sessionKey,
  resultno,
  onSelect,
  isSelectedRateOption,
  selectedRoomIndex,
  doAllowBookFromBrochure,
}) => {
  const classes = useStyles();
  const { width } = useWindowSize();
  const { staterooms } = useSelector((state) => state.tripSearch.searchObj);
  const [isMobile, setIsMobile] = useState(false);
  const doAllowSelectCabin = onSelect !== null && !doAllowBookFromBrochure;
  const dispatch = useDispatch();
  const {
    availableCabins,
    availableCabinsCruiseLineAdvisories,
    availableCabinsErrors,
    currentTripId,
  } = useSelector((state) => state.cruise);

  const selectedTrip = useSelector((state) => state.trip.selectedTrips[currentTripId]);
  const cruiseDetails = useSelector((state) => state.cruise.cruiseDetails);
  const [selectedDeck, setSelectedDeck] = useState(null);
  const [doShowDeckPlan, setDoShowDeckPlan] = useState(false);
  const [bedTypeByCabin, setBedTypeByCabin] = useState({});
  const [doShowConnectingCabins, setDoShowConnectingCabins] = useState(false);
  const [doShowAccessibleCabins, setDoShowAccessibleCabins] = useState(false);
  const [filteredCabins, setFilteredCabins] = useState(null);
  const [selectedCabinNumber, seSelectedCabinNumber] = useState(null);
  const [clickedCabinNumber, setClickedCabinNumber] = useState(null);
  const selectedCabinTimer = useRef();
  const rowsRef = useScrollToElement({
    value: !!clickedCabinNumber,
    options: {
      block: 'center',
      behavior: 'smooth',
    },
  });

  useEffect(() => {
    if (width < 1024) {
      setIsMobile(true);
      return;
    }
    setIsMobile(false);
  }, [width]);

  const setSelectedBedType = useCallback(
    (cabinNumber, bedTypeCode) => {
      setBedTypeByCabin({
        ...bedTypeByCabin,
        [cabinNumber]: bedTypeCode,
      });
    },
    [bedTypeByCabin],
  );

  const filterCabinsByAccessibleAndConnected = (
    _doShowAccessibleCabins,
    _doShowConnectingCabins,
  ) => {
    if (
      availableCabins &&
      availableCabins.length &&
      (_doShowAccessibleCabins || _doShowConnectingCabins)
    ) {
      setFilteredCabins(
        _.filter(availableCabins, (c) => {
          if (_doShowAccessibleCabins && c.isHandicapAccessible) return true;
          if (_doShowConnectingCabins && c.connectingStateroom) return true;
          return false;
        }),
      );
    } else {
      setFilteredCabins(null);
    }
  };

  const checkIsSelected = useCallback(
    (cabin) => {
      if (
        selectedTrip &&
        selectedTrip.cruiseItinerary &&
        selectedTrip.cruiseItinerary.cabins &&
        selectedTrip.cruiseItinerary.cabins[selectedRoomIndex]
      ) {
        return (
          selectedTrip.cruiseItinerary.cabins[selectedRoomIndex].selectedShipCabinCategory
            .cabinCategoryCode === cabinCategoryCode &&
          selectedTrip.cruiseItinerary.cabins[selectedRoomIndex].selectedRateOption
            .rateOptionCode === cabinCategoryRateOptionCode &&
          selectedTrip.cruiseItinerary.cabins[selectedRoomIndex].selectedShipCabin.cabinNumber ===
            cabin.cabinNumber &&
          selectedTrip.cruiseItinerary.cabins[selectedRoomIndex].selectedShipCabin.cabinDeckName ===
            cabin.cabinDeckName
        );
      }
      return false;
    },
    [selectedTrip, selectedRoomIndex, cabinCategoryCode, cabinCategoryRateOptionCode],
  );

  const checkIsSelectedInAnotherStateroom = useCallback(
    (cabin) => {
      if (
        selectedTrip &&
        selectedTrip.cruiseItinerary &&
        selectedTrip.cruiseItinerary.cabins &&
        selectedTrip.cruiseItinerary.cabins.length > 0
      ) {
        const selectedCabin = _.find(
          selectedTrip.cruiseItinerary.cabins,
          (c) =>
            c &&
            c.roomIndex !== selectedRoomIndex &&
            cabin.cabinDeckName &&
            cabin.cabinDeckName !== 'GUAR' &&
            cabin.cabinDeckName !== 'GTY' &&
            (cabin.cabinDeckName || '').toLowerCase().indexOf('to be assigned') === -1 &&
            c.selectedShipCabinCategory.cabinCategoryCode === cabinCategoryCode &&
            c.selectedRateOption.rateOptionCode === cabinCategoryRateOptionCode &&
            c.selectedShipCabin.cabinNumber === cabin.cabinNumber &&
            c.selectedShipCabin.cabinDeckName === cabin.cabinDeckName,
        );
        return selectedCabin && selectedCabin.roomIndex > -1 ? selectedCabin : null;
      }
      return null;
    },
    [selectedTrip, selectedRoomIndex, cabinCategoryCode, cabinCategoryRateOptionCode],
  );

  const handleRemoveFromTrip = useCallback(
    (roomIndex) => {
      if (selectedTrip.cruiseItinerary.cabins.filter((c) => c).length <= 1) {
        dispatch(addToTrip({ tripId: currentTripId, trip: null }));
        return;
      }
      dispatch(
        removeFromTrip({
          currentTripId,
          key: 'cruiseItinerary',
          selectedRoomIndex: roomIndex,
        }),
      );
    },
    [dispatch, selectedTrip, currentTripId],
  );

  const { track } = useAnalytics();

  const handleCabinDeckNameClick =
    (name, cabinNumber = null) =>
    () => {
      if (name && cruiseDetails && cruiseDetails.ship && cruiseDetails.ship.shipDecks) {
        const deck = cruiseDetails.ship.shipDecks.find((d) => areDecksEqual(d, name));

        seSelectedCabinNumber(cabinNumber);

        setSelectedDeck(deck);
      }
      setDoShowDeckPlan(true);
      track('cabinSelect.handleCabinDeckNameClick', {
        name,
      });
    };

  const handleCloseDeckPlan = useCallback(() => {
    setSelectedDeck(null);
    setDoShowDeckPlan(false);
    seSelectedCabinNumber(null);
    track('cabinSelect.handleCloseDeckPlan');
  }, [setSelectedDeck, setDoShowDeckPlan, seSelectedCabinNumber, track]);

  useEffect(() => {
    handleCloseDeckPlan();
  }, [isMobile, handleCloseDeckPlan]);

  const handleBedTypeChange = (cabin, bedTypeCode) => {
    setSelectedBedType(cabin.cabinNumber, bedTypeCode);
    if (checkIsSelected(cabin)) {
      onSelect(cabin, bedTypeCode, true);
    }
  };

  const handleCabinFromDeckClick = (cabinNumber) => {
    const cabin = (filteredCabins || availableCabins).find((c) => c.cabinNumber === cabinNumber);
    if (cabin) {
      clearTimeout(selectedCabinTimer.current);
      setClickedCabinNumber(cabin.cabinNumber);
      selectedCabinTimer.current = setTimeout(() => {
        setClickedCabinNumber(null);
      }, 3000);
    }
  };

  useEffect(() => {
    dispatch(
      availableCabinsRequest({
        itineraryId,
        cabinCategoryCode,
        cabinCategoryRateOptionCode,
        shipId: cruiseDetails && cruiseDetails.ship && cruiseDetails.ship.shipId,
        ...formatStateroomsForRequest(
          staterooms[selectedRoomIndex],
          cruiseDetails && cruiseDetails.cruiseLine.cruiseLineId,
        ),
        gradeno,
        sessionKey,
        resultno,
      }),
    );
  }, [
    dispatch,
    itineraryId,
    cabinCategoryCode,
    cabinCategoryRateOptionCode,
    staterooms,
    selectedRoomIndex,
    cruiseDetails,
    resultno,
    gradeno,
    sessionKey,
  ]);

  useEffect(() => {
    if (
      selectedTrip &&
      selectedTrip.cruiseItinerary &&
      selectedTrip.cruiseItinerary.cabins &&
      selectedTrip.cruiseItinerary.cabins[selectedRoomIndex]
    ) {
      setSelectedBedType(
        selectedTrip.cruiseItinerary.cabins[selectedRoomIndex].selectedShipCabin.cabinNumber,
        selectedTrip.cruiseItinerary.cabins[selectedRoomIndex].selectedShipCabin
          .selectedCabinBedTypeCode,
      );
    }
    // oxlint-disable-next-line react/exhaustive-deps
  }, []);

  useEffect(
    () => () => {
      clearTimeout(selectedCabinTimer);
    },
    [],
  );

  // TODO: probably better to create a component for table
  // this table is nearly identical in cruise information

  const renderComponent = () => (
    <>
      {!availableCabinsErrors && availableCabins && availableCabins.length ? (
        <div>
          <span className="font-bold">Select your cabin</span>
          <CruiseLineAdvisories
            advisories={availableCabinsCruiseLineAdvisories || []}
            className="p-2 mt-2"
          />
          <div>
            <Checkbox
              checked={doShowConnectingCabins}
              onChange={(event, newVal) => {
                setDoShowConnectingCabins(newVal);
                filterCabinsByAccessibleAndConnected(doShowAccessibleCabins, newVal);
              }}
              label="Show connecting cabins"
            />
            <Checkbox
              checked={doShowAccessibleCabins}
              onChange={(event, newVal) => {
                setDoShowAccessibleCabins(newVal);
                filterCabinsByAccessibleAndConnected(newVal, doShowConnectingCabins);
              }}
              label="Show handicap accessible cabins"
            />
          </div>
          <div className="grid grid-cols-2 border-2 mt-2">
            <div
              className={clsx(
                !doShowDeckPlan ? 'col-span-2' : 'col-span-2 lg:col-span-1',
                'overflow-x-auto',
              )}
            >
              <table className="table-auto text-left border-r-2 w-full text-sm md:text-base">
                <thead className="uppercase bg-gray-100 border-b-2 h-10">
                  <tr>
                    <th className="p-5">Number</th>
                    <th className="p-2">Bed types</th>
                    <th className="p-2">Deck</th>
                    <th className="p-2">Location</th>

                    {doAllowSelectCabin && (
                      // oxlint-disable-next-line jsx-a11y/control-has-associated-label
                      <th className="pl-2 pr-5" />
                    )}
                  </tr>
                </thead>

                <tbody className="bg-white">
                  {(!filteredCabins || filteredCabins.length === 0) &&
                  (doShowAccessibleCabins || doShowConnectingCabins) ? (
                    <tr>
                      <td colSpan="5" className="pl-5 pt-4">
                        Can not find any
                        {doShowAccessibleCabins ? ' handicap accessible ' : ''}
                        {doShowAccessibleCabins && doShowConnectingCabins ? ' or ' : ''}
                        {doShowConnectingCabins ? ' connecting ' : ''}
                        cabins
                      </td>
                    </tr>
                  ) : (
                    (filteredCabins || availableCabins).map((cabin) => (
                      <Fragment key={cabin.cabinNumber}>
                        <tr
                          ref={cabin.cabinNumber === clickedCabinNumber ? rowsRef : undefined}
                          className={`h-16 cursor-default
                            ${clsx({
                              'border-b-2': !isMobile || selectedCabinNumber !== cabin.cabinNumber,
                              'bg-theme bg-opacity-10': cabin.cabinNumber === clickedCabinNumber,
                            })}
                            `}
                        >
                          <td className="p-5">
                            {cabin.cabinNumber}
                            {cabin.isHandicapAccessible && (
                              <Tooltip
                                title="Handicap accessible cabin"
                                arrow
                                placement="top"
                                classes={classes}
                              >
                                <span>
                                  <MdAccessible className="text-2xl inline text-theme ml-1 -mt-1" />
                                </span>
                              </Tooltip>
                            )}
                            {cabin.connectingStateroom && (
                              <Tooltip
                                title={`Connected with cabin ${cabin.connectingStateroom}`}
                                arrow
                                placement="top"
                                classes={classes}
                              >
                                <span>
                                  <CgArrowsVAlt className="text-2xl inline text-theme ml-1 -mt-1" />
                                </span>
                              </Tooltip>
                            )}
                          </td>
                          <td className="p-2">
                            {cabin.cabinBedTypeOptions && cabin.cabinBedTypeOptions.length > 1 ? (
                              <Select
                                native
                                value={
                                  bedTypeByCabin[cabin.cabinNumber] ||
                                  cabin.cabinBedTypes[0].bedTypeCode
                                }
                                onChange={(event) => {
                                  handleBedTypeChange(cabin, event.target.value);
                                }}
                              >
                                {cabin.cabinBedTypes.map((bedType) => (
                                  <option value={bedType.bedTypeCode} key={bedType.bedTypeCode}>
                                    {bedType.bedTypeName}{' '}
                                  </option>
                                ))}
                              </Select>
                            ) : (
                              (cabin.cabinBedTypeOptions || []).join(',    ')
                            )}
                          </td>
                          <td className="p-2">
                            <a
                              className="text-theme cursor-pointer"
                              role="button"
                              tabIndex="-1"
                              onClick={handleCabinDeckNameClick(
                                cabin.cabinDeckName,
                                cabin.cabinNumber,
                              )}
                              onKeyDown={handleCabinDeckNameClick(
                                cabin.cabinDeckName,
                                cabin.cabinNumber,
                              )}
                            >
                              {cabin.cabinDeckName}
                            </a>
                          </td>
                          <td className={clsx('p-2')}>
                            {cabin.cabinLocations ? cabin.cabinLocations.join(', ') : 'N/A'}
                          </td>
                          {doAllowSelectCabin && (
                            <Tooltip
                              open={cabin.cabinNumber === clickedCabinNumber}
                              placement="top"
                              title="You have selected this cabin. please review the details and click `Select` to confirm the selection"
                              arrow
                              classes={classes}
                            >
                              <td className="pl-2 pr-5 text-right">
                                {checkIsSelectedInAnotherStateroom(cabin) && doAllowSelectCabin ? (
                                  <Label
                                    text={`Selected as stateroom ${
                                      checkIsSelectedInAnotherStateroom(cabin).roomIndex + 1
                                    }`}
                                    level="success"
                                    icon={<FaCheck />}
                                  />
                                ) : (
                                  doAllowSelectCabin && (
                                    <AddButton
                                      isSelected={checkIsSelected(cabin)}
                                      onAdd={() => {
                                        onSelect(
                                          cabin,
                                          bedTypeByCabin && bedTypeByCabin[cabin.cabinNumber],
                                        );
                                      }}
                                      onRemove={() => {
                                        handleRemoveFromTrip(selectedRoomIndex);
                                      }}
                                      addLabel="Select"
                                      removeLabel="Remove"
                                    />
                                  )
                                )}
                              </td>
                            </Tooltip>
                          )}
                          {doAllowBookFromBrochure && cabin && (
                            <td>
                              <div className="w-full my-4 px-2 flex items-center justify-center">
                                <TripBookButton
                                  buttonColorCssClass="bg-button-theme text-button-theme"
                                  buttonGroupStyles={{
                                    minWidth: 'unset',
                                    paddingTop: '0.25rem',
                                    paddingBottom: '0.25rem',
                                  }}
                                  onBookClick={() =>
                                    onSelect(
                                      cabin,
                                      bedTypeByCabin && bedTypeByCabin[cabin.cabinNumber],
                                    )
                                  }
                                />
                              </div>
                            </td>
                          )}
                        </tr>
                        {isMobile && selectedCabinNumber === cabin.cabinNumber && (
                          <tr>
                            <td colSpan="5" className="border-b-2">
                              <DeckPlan
                                deckPlan={selectedDeck}
                                onClose={handleCloseDeckPlan}
                                deckPlans={cruiseDetails.ship.shipDecks}
                                onSelect={handleCabinFromDeckClick}
                                key={cabin.cabinNumber}
                                selectedStateroomIndex={selectedRoomIndex}
                                filteredCabins={filteredCabins}
                              />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))
                  )}
                  <>
                    <SpecificCabinSelect
                      checkIsSelected={checkIsSelected}
                      onSelect={onSelect}
                      onRemoveFromTrip={handleRemoveFromTrip}
                      doShowDeckPlan={doShowDeckPlan}
                      itineraryId={itineraryId}
                      cabinCategoryCode={cabinCategoryCode}
                      cabinCategoryRateOptionCode={cabinCategoryRateOptionCode}
                      onCabinDeckNameClick={handleCabinDeckNameClick}
                      isSelectedRateOption={isSelectedRateOption}
                      selectedRoomIndex={selectedRoomIndex}
                      gradeno={gradeno}
                      resultno={resultno}
                      sessionKey={sessionKey}
                    />
                    {isMobile && selectedDeck && !selectedCabinNumber && (
                      <tr>
                        <td colSpan="5" className="border-b-2">
                          <DeckPlan
                            key={selectedDeck.cabinNumber}
                            deckPlan={selectedDeck}
                            onClose={handleCloseDeckPlan}
                            deckPlans={cruiseDetails.ship.shipDecks}
                            onSelect={handleCabinFromDeckClick}
                            selectedStateroomIndex={selectedRoomIndex}
                            filteredCabins={filteredCabins}
                          />
                        </td>
                      </tr>
                    )}
                  </>
                </tbody>
              </table>
            </div>
            <div className="bg-white text-center">
              {doShowDeckPlan && !isMobile && (
                <DeckPlan
                  deckPlan={selectedDeck}
                  onClose={handleCloseDeckPlan}
                  deckPlans={cruiseDetails.ship.shipDecks}
                  onSelect={handleCabinFromDeckClick}
                  key={itineraryId + cabinCategoryCode + cabinCategoryRateOptionCode}
                  selectedStateroomIndex={selectedRoomIndex}
                  filteredCabins={filteredCabins}
                />
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="text-gray-600 font-bold my-10 mx-5">
          No cabins found.{' '}
          {availableCabinsErrors && availableCabinsErrors.map((e) => e.description)}
        </div>
      )}
    </>
  );

  return withSpinner(
    renderComponent,
    !availableCabinsErrors && (availableCabins || []).length === 0,
  );
};

CabinSelect.propTypes = {
  itineraryId: PropTypes.string.isRequired,
  cabinCategoryCode: PropTypes.string.isRequired,
  gradeno: PropTypes.string.isRequired,
  resultno: PropTypes.string.isRequired,
  sessionKey: PropTypes.string.isRequired,
  cabinCategoryRateOptionCode: PropTypes.string.isRequired,
  onSelect: PropTypes.func,
  isSelectedRateOption: PropTypes.bool,
  selectedRoomIndex: PropTypes.number.isRequired,
  doAllowBookFromBrochure: PropTypes.bool,
};

CabinSelect.defaultProps = {
  isSelectedRateOption: false,
  onSelect: undefined,
  doAllowBookFromBrochure: false,
};

export default memo(CabinSelect);
